import { type Registry as Services, service } from '@ember/service';
import { attr } from '@ember-data/model';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { Type } from '@warp-drive/core-types/symbols';
import { tKey } from 'ember-intl';
import { BenchmarkInsightCompetitorsCompset } from 'lumen/models';

export const PRIORITIZED_CATEGORIES = {
  RECOMMENDED: 'recommended',
  ADR: 'adr',
  HOTEL_CLASS: 'hotel_class',
  DISTANCE: 'distance',
} as const;

export type PrioritizedCategory =
  (typeof PRIORITIZED_CATEGORIES)[keyof typeof PRIORITIZED_CATEGORIES];

export const isPrioritizedCategory = (
  value: string,
): value is PrioritizedCategory => {
  return Object.values(PRIORITIZED_CATEGORIES).includes(
    value as PrioritizedCategory,
  );
};

const PROPOSED_COMPSET_NAME_TRANSLATIONS: Record<PrioritizedCategory, string> =
  {
    [PRIORITIZED_CATEGORIES.RECOMMENDED]: tKey(
      'settings.competitors.benchmark.compset.builder.proposals.compset_name.recommended',
    ),
    [PRIORITIZED_CATEGORIES.ADR]: tKey(
      'settings.competitors.benchmark.compset.builder.proposals.compset_name.adr',
    ),
    [PRIORITIZED_CATEGORIES.HOTEL_CLASS]: tKey(
      'settings.competitors.benchmark.compset.builder.proposals.compset_name.hotel_class',
    ),
    [PRIORITIZED_CATEGORIES.DISTANCE]: tKey(
      'settings.competitors.benchmark.compset.builder.proposals.compset_name.distance',
    ),
  };

const PROPOSED_COMPSET_DESCRIPTION_TRANSLATIONS: Record<
  PrioritizedCategory,
  string
> = {
  [PRIORITIZED_CATEGORIES.RECOMMENDED]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.compset_description.recommended',
  ),
  [PRIORITIZED_CATEGORIES.ADR]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.compset_description.adr',
  ),
  [PRIORITIZED_CATEGORIES.HOTEL_CLASS]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.compset_description.hotel_class',
  ),
  [PRIORITIZED_CATEGORIES.DISTANCE]: tKey(
    'settings.competitors.benchmark.compset.builder.proposals.compset_description.distance',
  ),
};

const PROPOSED_COMPSET_ICONS: Record<PrioritizedCategory, IconName> = {
  [PRIORITIZED_CATEGORIES.RECOMMENDED]: 'badge',
  [PRIORITIZED_CATEGORIES.ADR]: 'rates-lowest',
  [PRIORITIZED_CATEGORIES.HOTEL_CLASS]: 'star-filled',
  [PRIORITIZED_CATEGORIES.DISTANCE]: 'location-pin',
};

export default class ProposedCompsetModel extends BenchmarkInsightCompetitorsCompset {
  // @ts-expect-error: Class inheritance
  [Type] = 'benchmark-insight/competitors/proposed-compset' as const;

  @attr('string') declare prioritizedCategory: PrioritizedCategory;

  @service declare intl: Services['intl'];

  get description(): string {
    return this.intl.t(
      PROPOSED_COMPSET_DESCRIPTION_TRANSLATIONS[this.prioritizedCategory],
    );
  }

  get icon(): IconName {
    return PROPOSED_COMPSET_ICONS[this.prioritizedCategory];
  }

  get name(): string {
    return this.intl.t(
      PROPOSED_COMPSET_NAME_TRANSLATIONS[this.prioritizedCategory],
    );
  }
}
